/**
 * Adopting an imported finding: the one door out of the quarantine.
 *
 * Its own file rather than a section of `findingCard.tsx`, which is *one
 * finding* — its claim, its edges, the citation checks against it. Adoption is a
 * different act with its own rules, and the rules are the reason it needs room:
 * a copy may never claim more maturity than the original, a retired original
 * cannot be adopted at all, and the author's name comes across unchanged. Each
 * of those has to be visible in the interface at the moment the reader decides,
 * not discovered afterwards from a refusal.
 *
 * The status picker offers only what the server would accept, computed there
 * from the original's own status and sent with the finding. Offering the whole
 * ladder and rejecting the illegal half would teach the rule at the worst
 * possible moment — after the reader has already said what they believe.
 */

import type { JSX } from 'react'
import { useState } from 'react'

import type { AdoptionOffer, FindingDetail, FindingStatus } from '../account'
import { adoptFinding } from '../account'
import type { Card } from '../cards'
import { t } from '../i18n'

/** What this reader may do about this import — three answers, one with a form. */
export function Adoption({
  finding,
  offer,
  onOpen,
  onAdopted,
}: {
  finding: FindingDetail
  offer: AdoptionOffer
  onOpen: (card: Card) => void
  onAdopted: () => void
}): JSX.Element {
  if (offer.adopted_id !== null) {
    const adoptedId = offer.adopted_id
    return (
      <p className="claim__meta">
        <span className="badge">{t('adopt.already')}</span>
        <button
          type="button"
          className="link"
          onClick={() => onOpen({ kind: 'finding', id: adoptedId })}
        >
          {offer.adopted_as}
        </button>
      </p>
    )
  }

  // An empty offer means a retired original: its author withdrew the claim, so
  // believing it is writing your own finding rather than copying theirs.
  if (offer.statuses.length === 0) {
    return <p className="muted">{t('adopt.retired')}</p>
  }

  return (
    <AdoptForm finding={finding} offer={offer} onOpen={onOpen} onAdopted={onAdopted} />
  )
}

/**
 * The choice itself, separated from the two states that have no choice in them.
 *
 * Splitting them also stops this form's state existing at all on a finding that
 * is already adopted — where a half-made choice would otherwise sit in memory
 * behind a branch that never renders it.
 */
function AdoptForm({
  finding,
  offer,
  onOpen,
  onAdopted,
}: {
  finding: FindingDetail
  offer: AdoptionOffer
  onOpen: (card: Card) => void
  onAdopted: () => void
}): JSX.Element {
  const [chosen, setChosen] = useState<FindingStatus | ''>('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  async function adopt(status: FindingStatus) {
    setBusy(true)
    setFailure(null)
    try {
      const copy = await adoptFinding(finding.id, status)
      onAdopted()
      onOpen({ kind: 'finding', id: copy.id })
    } catch (error) {
      setFailure(error instanceof Error ? error.message : t('adopt.failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="adopt">
      <h4>{t('adopt.heading')}</h4>
      {/* Said before the copy exists, not after it has travelled in a pack:
          adopting keeps the author's name, because deciding you believe a
          reading does not make the reasoning yours. */}
      <p className="muted">{t('adopt.explanation', { author: finding.origin ?? '' })}</p>
      <p className="claim__meta">
        <StatusPicker
          id={`adopt-status-${finding.id}`}
          statuses={offer.statuses}
          chosen={chosen}
          onChoose={setChosen}
        />
        <button
          type="button"
          className="link"
          disabled={busy || chosen === ''}
          onClick={() => {
            if (chosen !== '') void adopt(chosen)
          }}
        >
          {t('adopt.confirm')}
        </button>
      </p>
      {failure !== null && <p className="muted">{failure}</p>}
    </section>
  )
}

/**
 * How settled the reader holds the claim to be — capped at the original's.
 *
 * `''` is the unchosen state and is deliberate: there is no default, because a
 * default would answer on the reader's behalf the one question adoption exists
 * to ask them.
 */
function StatusPicker({
  id,
  statuses,
  chosen,
  onChoose,
}: {
  id: string
  statuses: FindingStatus[]
  chosen: FindingStatus | ''
  onChoose: (status: FindingStatus | '') => void
}): JSX.Element {
  return (
    <>
      <label htmlFor={id} className="muted">
        {t('adopt.statusLabel')}
      </label>
      <select
        id={id}
        value={chosen}
        onChange={(event) => onChoose(event.target.value as FindingStatus | '')}
      >
        <option value="">{t('adopt.statusUnchosen')}</option>
        {statuses.map((status) => (
          <option key={status} value={status}>
            {t(`findings.status.${status}`)}
          </option>
        ))}
      </select>
    </>
  )
}
